/**
 * Tab «Agente» delle impostazioni M3: identità di Aida, blocco invii esterni,
 * whitelist mittenti, memorie apprese (con conferma esplicita delle proposte).
 */

import { Bot, FlaskConical, Loader2, Plus, Save, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import {
  useM3AggiornaImpostazioni,
  useM3CreaMemoria,
  useM3Impostazioni,
  useM3MemorieAgente,
  useM3ToggleMemoria,
  useM3UpsertWhitelist,
  useM3Whitelist,
} from "@/hooks/useM3";
import { cn } from "@/lib/utils";

const INPUT =
  "h-9 w-full rounded-md border bg-card px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

export function AgenteSection() {
  return (
    <div className="space-y-8">
      <IdentitaAgente />
      <WhitelistSection />
      <MemorieSection />
    </div>
  );
}

function IdentitaAgente() {
  const { data: imp, isLoading } = useM3Impostazioni();
  const aggiorna = useM3AggiornaImpostazioni();
  const [nome, setNome] = useState("");
  const [indirizzo, setIndirizzo] = useState("");
  const [firma, setFirma] = useState("");
  const [blocco, setBlocco] = useState(true);
  const [sospeso, setSospeso] = useState(false);

  useEffect(() => {
    if (imp) {
      setNome(imp.agente_nome);
      setIndirizzo(imp.agente_indirizzo_email ?? "");
      setFirma(imp.agente_firma);
      setBlocco(imp.invio_esterno_bloccato);
      setSospeso(imp.invio_uscita_sospeso);
    }
  }, [imp]);

  if (isLoading) return <Skeleton className="h-40 w-full" />;

  return (
    <section className="space-y-3">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <Bot className="size-4" /> Identità dell'agente
      </h2>
      <div className="grid max-w-3xl gap-3 sm:grid-cols-2">
        <label className="space-y-1 text-xs text-muted-foreground">
          <span>Nome</span>
          <input className={INPUT} value={nome} onChange={(e) => setNome(e.target.value)} />
        </label>
        <label className="space-y-1 text-xs text-muted-foreground">
          <span>Indirizzo email di risposta (Reply-To)</span>
          <input
            className={INPUT}
            value={indirizzo}
            onChange={(e) => setIndirizzo(e.target.value)}
            placeholder="aida@misterpet.maitime.ai"
          />
        </label>
        <label className="space-y-1 text-xs text-muted-foreground sm:col-span-2">
          <span>Firma in calce alle email</span>
          <input className={INPUT} value={firma} onChange={(e) => setFirma(e.target.value)} />
        </label>
      </div>
      <label className="flex max-w-3xl items-start gap-2 rounded-md border bg-muted/20 px-3 py-2.5">
        <input
          type="checkbox"
          checked={blocco}
          onChange={(e) => setBlocco(e.target.checked)}
          className="mt-0.5"
        />
        <span className="text-xs">
          <span className="flex items-center gap-1 font-medium text-foreground">
            <ShieldCheck className="size-3.5" /> Invio esterno bloccato
          </span>
          <span className="text-muted-foreground">
            Finché attivo, l'agente scrive SOLO agli indirizzi in whitelist: la
            conferma documenti va a Marina, che inoltra lei a clienti/agenzia.
            Disattivarlo è una decisione di prodotto, non un'opzione operativa.
          </span>
        </span>
      </label>
      <label
        className={cn(
          "flex max-w-3xl items-start gap-2 rounded-md border px-3 py-2.5",
          sospeso ? "border-amber-300 bg-amber-50/60" : "bg-muted/20"
        )}
      >
        <input
          type="checkbox"
          checked={sospeso}
          onChange={(e) => setSospeso(e.target.checked)}
          className="mt-0.5"
        />
        <span className="text-xs">
          <span className="flex items-center gap-1 font-medium text-foreground">
            <FlaskConical className="size-3.5" /> Modalità test — invio email sospeso
          </span>
          <span className="text-muted-foreground">
            Finché attivo, l'agente elabora e <em>compone</em> le email normalmente
            ma NON le spedisce a nessuno: vengono marcate «trattenuta» e restano
            ispezionabili nel canale Email. Serve a verificarne il comportamento
            senza far uscire nulla.
          </span>
        </span>
      </label>
      <button
        type="button"
        disabled={aggiorna.isPending}
        onClick={() =>
          aggiorna.mutate({
            agente_nome: nome || "Aida",
            agente_indirizzo_email: indirizzo || undefined,
            agente_firma: firma || undefined,
            invio_esterno_bloccato: blocco,
            invio_uscita_sospeso: sospeso,
          })
        }
        className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
      >
        {aggiorna.isPending ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : (
          <Save className="size-3.5" />
        )}
        Salva identità
      </button>
    </section>
  );
}

function WhitelistSection() {
  const { data: whitelist, isLoading } = useM3Whitelist();
  const upsert = useM3UpsertWhitelist();
  const [nuovaEmail, setNuovaEmail] = useState("");
  const [nuovaDescr, setNuovaDescr] = useState("");

  if (isLoading) return <Skeleton className="h-32 w-full" />;

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold text-foreground">
        Whitelist mittenti ({whitelist?.filter((w) => w.attivo).length ?? 0} attivi)
      </h2>
      <p className="max-w-3xl text-xs text-muted-foreground">
        Solo le email da questi indirizzi vengono elaborate; le altre sono
        registrate e scartate, senza risposta e senza passare dall'AI.
      </p>
      <div className="max-w-3xl space-y-1.5">
        {(whitelist ?? []).map((w) => (
          <div
            key={w.email}
            className="flex items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm"
          >
            <span className={cn("font-mono text-xs", !w.attivo && "line-through opacity-50")}>
              {w.email}
            </span>
            <span className="truncate text-xs text-muted-foreground">{w.descrizione}</span>
            <button
              type="button"
              onClick={() =>
                upsert.mutate({
                  email: w.email,
                  descrizione: w.descrizione ?? undefined,
                  attivo: !w.attivo,
                })
              }
              className={cn(
                "ml-auto rounded border px-2 py-0.5 text-[11px]",
                w.attivo
                  ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                  : "border-border bg-muted text-muted-foreground"
              )}
            >
              {w.attivo ? "Attivo" : "Disattivato"}
            </button>
          </div>
        ))}
        <div className="flex items-center gap-2">
          <input
            className={INPUT}
            placeholder="email@misterpetsrl.com"
            value={nuovaEmail}
            onChange={(e) => setNuovaEmail(e.target.value)}
          />
          <input
            className={INPUT}
            placeholder="Descrizione (es. Marina — logistica)"
            value={nuovaDescr}
            onChange={(e) => setNuovaDescr(e.target.value)}
          />
          <button
            type="button"
            disabled={!nuovaEmail.includes("@") || upsert.isPending}
            onClick={() => {
              upsert.mutate({ email: nuovaEmail, descrizione: nuovaDescr || undefined });
              setNuovaEmail("");
              setNuovaDescr("");
            }}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border px-2.5 py-2 text-xs hover:bg-accent disabled:opacity-50"
          >
            <Plus className="size-3.5" /> Aggiungi
          </button>
        </div>
      </div>
    </section>
  );
}

const SCOPE_LABEL: Record<string, string> = {
  globale: "globale",
  paese: "paese",
  cliente: "cliente",
};

function MemorieSection() {
  const { data: memorie, isLoading } = useM3MemorieAgente();
  const toggle = useM3ToggleMemoria();
  const crea = useM3CreaMemoria();
  const [nuova, setNuova] = useState("");

  if (isLoading) return <Skeleton className="h-32 w-full" />;

  const proposte = (memorie ?? []).filter((m) => !m.attiva && m.origine !== "istruzione");
  const attive = (memorie ?? []).filter((m) => m.attiva);
  const disattivate = (memorie ?? []).filter(
    (m) => !m.attiva && m.origine === "istruzione"
  );

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold text-foreground">
        Memorie dell'agente ({attive.length} attive
        {proposte.length > 0 ? ` · ${proposte.length} da confermare` : ""})
      </h2>
      <p className="max-w-3xl text-xs text-muted-foreground">
        Regole apprese dalle correzioni (con conferma esplicita) o impartite
        come istruzioni. Le memorie attive entrano nel contesto dell'agente.
      </p>
      <div className="max-w-3xl space-y-1.5">
        {proposte.map((m) => (
          <div
            key={m.id}
            className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50/60 px-3 py-2 text-sm"
          >
            <div className="min-w-0 flex-1">
              <div className="text-[13px]">{m.contenuto}</div>
              <div className="text-[11px] text-muted-foreground">
                proposta da correzione · scope {SCOPE_LABEL[m.scope]}
                {m.scope_ref ? ` (${m.scope_ref})` : ""}
              </div>
            </div>
            <button
              type="button"
              onClick={() => toggle.mutate({ memoriaId: m.id, attiva: true })}
              className="shrink-0 rounded border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-800 hover:bg-emerald-100"
            >
              Conferma
            </button>
          </div>
        ))}
        {[...attive, ...disattivate].map((m) => (
          <div
            key={m.id}
            className="flex items-start gap-2 rounded-md border bg-card px-3 py-2 text-sm"
          >
            <div className="min-w-0 flex-1">
              <div className={cn("text-[13px]", !m.attiva && "opacity-50")}>{m.contenuto}</div>
              <div className="text-[11px] text-muted-foreground">
                {m.origine.replaceAll("_", " ")} · scope {SCOPE_LABEL[m.scope]}
                {m.scope_ref ? ` (${m.scope_ref})` : ""}
              </div>
            </div>
            <button
              type="button"
              onClick={() => toggle.mutate({ memoriaId: m.id, attiva: !m.attiva })}
              className={cn(
                "shrink-0 rounded border px-2 py-0.5 text-[11px]",
                m.attiva
                  ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                  : "border-border bg-muted text-muted-foreground"
              )}
            >
              {m.attiva ? "Attiva" : "Disattivata"}
            </button>
          </div>
        ))}
        <div className="flex items-center gap-2">
          <input
            className={INPUT}
            placeholder="Nuova istruzione (es. «per FIDRIK i pesi con la virgola»)"
            value={nuova}
            onChange={(e) => setNuova(e.target.value)}
          />
          <button
            type="button"
            disabled={nuova.trim().length < 8 || crea.isPending}
            onClick={() => {
              crea.mutate({
                scope: "globale",
                chiave: nuova.toLowerCase().slice(0, 40).replaceAll(" ", "-"),
                contenuto: nuova.trim(),
                attiva: true,
              });
              setNuova("");
            }}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border px-2.5 py-2 text-xs hover:bg-accent disabled:opacity-50"
          >
            <Plus className="size-3.5" /> Istruzione
          </button>
        </div>
      </div>
    </section>
  );
}
